import { useEffect, useState } from "react";
import api from "../../services/api";

import {
  DashboardLayout,
  MainContent,
  StatsGrid,
  ActionGrid,
  HighlightSection,
  HighlightHeader,
  HighlightTitle,
  HighlightContentWrapper,
  HighlightContentSlider,
  HighlightContent,
  HighlightDetails,
  HighlightItemTitle,
  HighlightDescription,
  HighlightMeta,
  HighlightIcon,
} from "./styles";

import Sidebar from "../../components/sidebar/Sidebar";
import StatCard from "../../components/dash/startCard/StartCard";
import ActionCard from "../../components/dash/actionCard/ActionCard";

import { FiCalendar, FiBriefcase, FiBookOpen, FiUsers } from "react-icons/fi";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export default function Dash() {
  const [stats, setStats] = useState([]);
  const [actions, setActions] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const getHighlightIcon = (item) => {
    if (item.tipo) {
      switch (item.tipo) {
        case "evento":
          return <FiCalendar size={48} color="#437EFB" />;
        case "curso":
          return <FiBookOpen size={48} color="#437EFB" />;
        case "oportunidade":
          return <FiBriefcase size={48} color="#437EFB" />;
        default:
          return <FiUsers size={48} color="#437EFB" />;
      }
    }
    return <FiUsers size={48} color="#437EFB" />;
  };

  useEffect(() => {
    async function loadData() {
      try {
        const [eventosRes, cursosRes, oportunidadesRes] = await Promise.all([
          api.get("/api/content/eventos"),
          api.get("/api/content/cursos"),
          api.get("/api/content/oportunidades"),
        ]);

        const eventos = Array.isArray(eventosRes.data) ? eventosRes.data : [];
        const cursos = Array.isArray(cursosRes.data) ? cursosRes.data : [];
        const oportunidades = Array.isArray(oportunidadesRes.data) ? oportunidadesRes.data : [];

        setStats([
          { title: "Eventos", value: eventos.length, label: "disponíveis", icon: <FiCalendar size={22} color="#437EFB" /> },
          { title: "Cursos", value: cursos.length, label: "disponíveis", icon: <FiBookOpen size={22} color="#437EFB" /> },
          { title: "Oportunidades", value: oportunidades.length, label: "disponíveis", icon: <FiBriefcase size={22} color="#437EFB" /> },
        ]);

        const startOfWeek = dayjs().startOf("week");
        const endOfWeek = dayjs().endOf("week");

        // Destaques da semana com datas corretas
        const allHighlights = [
          // Eventos usam 'data'
          ...eventos
            .filter(e => e.destacado && dayjs.utc(e.data).local().isBetween(startOfWeek, endOfWeek, null, '[]'))
            .map(e => ({ ...e, tipo: "evento", highlightDate: e.data })),

          // Cursos usam 'dataInicio'
          ...cursos
            .filter(c => c.destacado && dayjs.utc(c.dataInicio).local().isBetween(startOfWeek, endOfWeek, null, '[]'))
            .map(c => ({ ...c, tipo: "curso", highlightDate: c.dataInicio })),

          // Oportunidades usam 'dataInicio'
          ...oportunidades
            .filter(o => o.destacado && dayjs.utc(o.dataInicio).local().isBetween(startOfWeek, endOfWeek, null, '[]'))
            .map(o => ({ ...o, tipo: "oportunidade", highlightDate: o.dataInicio })),
        ];

        setHighlights(allHighlights);

        setActions([
          { title: "Novo evento", description: "Adicionar novo evento à plataforma", icon: <FiCalendar size={22} color="#437EFB" />, type: "evento", buttonText: "Criar" },
          { title: "Nova oportunidade", description: "Adicionar nova oportunidade de trabalho", icon: <FiBriefcase size={22} color="#437EFB" />, type: "oportunidade", buttonText: "Criar" },
          { title: "Novo curso", description: "Adicionar novo curso disponível", icon: <FiBookOpen size={22} color="#437EFB" />, type: "curso", buttonText: "Criar" },
        ]);
      } catch (error) {
        console.error("Erro ao carregar dados do dashboard:", error);
      }
    }
    loadData();
  }, []);

  // Auto-slide a cada 5 segundos
  useEffect(() => {
    if (highlights.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % highlights.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [highlights]);

  return (
    <DashboardLayout>
      <Sidebar />
      <MainContent>
        {/* Estatísticas */}
        <StatsGrid>
          {stats.map((stat) => (
            <StatCard key={stat.title} {...stat} />
          ))}
        </StatsGrid>

        {/* Carrossel */}
        <HighlightSection>
          <HighlightHeader>
            <HighlightTitle>Destaques da semana</HighlightTitle>
          </HighlightHeader>

          {highlights.length > 0 ? (
            <HighlightContentWrapper>
              <HighlightContentSlider style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
                {highlights.map((highlight, index) => (
                  <HighlightContent key={index}>
                    <HighlightIcon>{getHighlightIcon(highlight)}</HighlightIcon>
                    <HighlightDetails>
                      <HighlightItemTitle>{highlight.titulo || highlight.title}</HighlightItemTitle>
                      <HighlightDescription>{highlight.descricao || highlight.description}</HighlightDescription>
                      {/* Data correta */}
                      <HighlightMeta>{dayjs.utc(highlight.highlightDate).local().format("DD/MM/YYYY")}</HighlightMeta>
                    </HighlightDetails>
                  </HighlightContent>
                ))}
              </HighlightContentSlider>
            </HighlightContentWrapper>
          ) : (
            <HighlightContent>
              Nenhum destaque esta semana.
            </HighlightContent>
          )}
        </HighlightSection>

        {/* Ações */}
        <ActionGrid>
          {actions.map((action) => (
            <ActionCard key={action.title} {...action} />
          ))}
        </ActionGrid>
      </MainContent>
    </DashboardLayout>
  );
}
